import { QueryCache, QueryClient, VueQueryPlugin, type VueQueryPluginOptions } from "@tanstack/vue-query";

import { isApiError } from "@/src/api/http";
import { setLastApiError } from "@/src/queries/errors";

/**
 * Server state lives here rather than in the store.
 *
 * The portal reads a lot of Celonis metadata that is expensive to fetch and rarely changes, and
 * the same data is requested by several unrelated components, so the defaults lean towards
 * sharing a cached result over refetching.
 */
export const queryClient = new QueryClient({
    // Failed reads feed the same global error channel as failed writes, so the cross-cutting
    // observers (offline routing, unexpected 500s) see both.
    queryCache: new QueryCache({
        onError: (error) => {
            if (isApiError(error)) {
                setLastApiError(error);
            }
        },
    }),
    defaultOptions: {
        queries: {
            // The backend already caches data pools, data models, tables and columns for a day,
            // so treating them as fresh for a few minutes only mirrors what the API would return.
            staleTime: 5 * 60 * 1000,
            gcTime: 30 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
                // A rejected request is a decision, not a hiccup: retrying a 403 or a 404 only
                // delays the error the user needs to see.
                if (isApiError(error) && error.status >= 400 && error.status < 500) {
                    return false;
                }
                return failureCount < 2;
            },
        },
        mutations: {
            retry: false,
        },
    },
});

export const vueQueryPlugin = VueQueryPlugin;

export const vueQueryOptions: VueQueryPluginOptions = { queryClient };
